// Market Data page — the quote registry
// Traders change quotes, not curves. Curves are derived objects.
import { useState, useEffect } from 'react';
import Header from '../../components/Header';
import { QuoteSpec, QUOTE_KINDS, QUOTE_TYPES, CURRENCIES, QuoteKind, QuoteType } from '../../lib/types';
import { getSavedQuotes, saveQuote, deleteQuote, generateQuoteId } from '../../lib/storage/quotes';
import { NewIcon, listStyles } from '../../components/lists/listStyles';
import CollectionEmptyState from '../../components/ui/CollectionEmptyState';
import FeedbackBanner from '../../components/ui/FeedbackBanner';
import PageHeader from '../../components/ui/PageHeader';
import { entityUi, getCreateLabel } from '../../components/ui/entityUi';
import { formStyles } from '../../components/ui/formStyles';

export default function MarketData() {
  const ui = entityUi.quote;
  const [quotes, setQuotes] = useState<QuoteSpec[]>([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editingQuote, setEditingQuote] = useState<QuoteSpec | null>(null);
  const [filterCurrency, setFilterCurrency] = useState<string>('');
  const [filterKind, setFilterKind] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');
  const [search, setSearch] = useState('');
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    setQuotes(getSavedQuotes());
  }, []);

  const filtered = quotes.filter(q => {
    if (filterCurrency && q.currency !== filterCurrency) return false;
    if (filterKind && q.kind !== filterKind) return false;
    if (filterType && (q.quote_type || 'Curve') !== filterType) return false;
    if (search && !q.id.toLowerCase().includes(search.toLowerCase()) && 
        !(q.label || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const handleSave = (quote: QuoteSpec) => {
    saveQuote(quote);
    setQuotes(getSavedQuotes());
    setShowEditor(false);
    setEditingQuote(null);
    setSuccess(editingQuote ? 'Quote updated' : 'Quote added');
    setTimeout(() => setSuccess(null), 2000);
  };

  const handleDelete = (id: string) => {
    if (!confirm('Delete this quote?')) return;
    deleteQuote(id);
    setQuotes(getSavedQuotes());
  };

  const handleEdit = (q: QuoteSpec) => {
    setEditingQuote(q);
    setShowEditor(true);
  };

  const formatValue = (q: QuoteSpec) => {
    if (q.kind === 'Rate' || q.kind === 'Spread') return `${(q.value * 100).toFixed(4)}%`;
    if (q.kind === 'FxSpot' || q.kind === 'FxPoints') return q.value.toFixed(4);
    return q.value.toFixed(2);
  };

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-12">
        <PageHeader
          title="Market Data"
          subtitle="Quote registry — rates, spreads, and prices used by curve construction"
          actions={
            <button
              onClick={() => { setEditingQuote(null); setShowEditor(true); }}
              className={listStyles.primaryNewButton}
            >
              <NewIcon />
              {getCreateLabel(ui)}
            </button>
          }
        />

        {success && <FeedbackBanner tone="success" message={success} className="mb-4 p-3" />}

        {/* Filters */}
        <div className="bg-white border border-[#e5e5e5] rounded-xl p-4 mb-4">
          <div className="flex gap-3 items-center flex-wrap">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search quotes..."
              className="px-3 py-1.5 text-sm border border-[#d4d4d4] rounded-lg focus:outline-none focus:border-[#8a6a2f] w-64"
            />
            <select value={filterKind} onChange={e => setFilterKind(e.target.value)}
              className="px-3 py-1.5 text-sm border border-[#d4d4d4] rounded-lg">
              <option value="">All types</option>
              {QUOTE_KINDS.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
            <select value={filterType} onChange={e => setFilterType(e.target.value)}
              className="px-3 py-1.5 text-sm border border-[#d4d4d4] rounded-lg">
              <option value="">All quote types</option>
              {QUOTE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={filterCurrency} onChange={e => setFilterCurrency(e.target.value)}
              className="px-3 py-1.5 text-sm border border-[#d4d4d4] rounded-lg">
              <option value="">All currencies</option>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <span className="text-xs text-[#a3a3a3] ml-auto">{filtered.length} quote{filtered.length !== 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* Quote Editor Modal */}
        {showEditor && (
          <QuoteEditor
            quote={editingQuote}
            onSave={handleSave}
            onCancel={() => { setShowEditor(false); setEditingQuote(null); }}
          />
        )}

        {/* Quotes Table */}
        {filtered.length === 0 ? (
          <CollectionEmptyState
            icon={ui.icon}
            title="No quotes yet"
            description={ui.emptyDescription}
            actionLabel={getCreateLabel(ui)}
            onAction={() => { setEditingQuote(null); setShowEditor(true); }}
          />
        ) : (
          <div className="bg-white border border-[#e5e5e5] rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#e5e5e5] bg-[#fafafa]">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#737373] uppercase tracking-wider">ID</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#737373] uppercase tracking-wider">Label</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#737373] uppercase tracking-wider">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#737373] uppercase tracking-wider">Quote Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[#737373] uppercase tracking-wider">CCY</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-[#737373] uppercase tracking-wider">Value</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-[#737373] uppercase tracking-wider w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(q => (
                  <tr key={q.id} className="border-b border-[#f5f5f5] hover:bg-[#fafafa] group">
                    <td className="px-4 py-3">
                      <code className="text-sm font-mono text-[#0a0a0a]">{q.id}</code>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#525252]">{q.label || q.description || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${
                        q.kind === 'Rate' ? 'bg-blue-50 text-blue-700' :
                        q.kind === 'Spread' ? 'bg-amber-50 text-amber-700' :
                        q.kind === 'Price' ? 'bg-green-50 text-green-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{q.kind}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#525252]">{q.quote_type || 'Curve'}</td>
                    <td className="px-4 py-3 text-sm text-[#737373]">{q.currency || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-mono text-sm font-medium text-[#0a0a0a]">{formatValue(q)}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => handleEdit(q)} className="p-1.5 text-[#737373] hover:text-[#0a0a0a] hover:bg-[#f5f5f5] rounded" title="Edit">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button onClick={() => handleDelete(q.id)} className="p-1.5 text-[#737373] hover:text-red-500 hover:bg-red-50 rounded" title="Delete">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

// ============================================================================
// Quote Editor inline panel
// ============================================================================

function QuoteEditor({ quote, onSave, onCancel }: {
  quote: QuoteSpec | null;
  onSave: (q: QuoteSpec) => void;
  onCancel: () => void;
}) {
  const [id, setId] = useState(quote?.id || '');
  const [kind, setKind] = useState<QuoteKind>(quote?.kind || 'Rate');
  const [quoteType, setQuoteType] = useState<QuoteType>(quote?.quote_type || 'Curve');
  const [value, setValue] = useState(quote ? 
    (quote.kind === 'Rate' || quote.kind === 'Spread' ? (quote.value * 100).toString() : quote.value.toString()) 
    : '');
  const [label, setLabel] = useState(quote?.label || '');
  const [currency, setCurrency] = useState(quote?.currency || 'EUR');
  const [description, setDescription] = useState(quote?.description || '');

  const isNew = !quote;

  const handleSubmit = () => {
    const qId = id.trim() || generateQuoteId();
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;

    const actualValue = (kind === 'Rate' || kind === 'Spread') ? numValue / 100 : numValue;

    onSave({
      id: qId,
      kind,
      value: actualValue,
      quote_type: quoteType,
      label: label.trim() || undefined,
      currency: currency || undefined,
      description: description.trim() || undefined,
      createdAt: quote?.createdAt,
      updatedAt: quote?.updatedAt,
    });
  };

  const inputClass = formStyles.input;
  const labelClass = formStyles.label;

  return (
    <div className="bg-white border border-[#e5e5e5] rounded-xl p-5 mb-4">
      <h3 className="text-sm font-semibold text-[#0a0a0a] mb-4">{isNew ? 'Add Quote' : 'Edit Quote'}</h3>
      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <label className={labelClass}>Quote ID *</label>
          <input
            type="text"
            value={id}
            onChange={e => setId(e.target.value.replace(/\s/g, '_').toUpperCase())}
            placeholder="e.g., EUR_OIS_5Y"
            className={inputClass}
            disabled={!isNew}
          />
          <p className="text-xs text-[#a3a3a3] mt-1">Unique identifier for this quote</p>
        </div>
        <div>
          <label className={labelClass}>Type</label>
          <select value={kind} onChange={e => setKind(e.target.value as QuoteKind)} className={inputClass}>
            {QUOTE_KINDS.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>Quote Type</label>
          <select value={quoteType} onChange={e => setQuoteType(e.target.value as QuoteType)} className={inputClass}>
            {QUOTE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>
            Value {kind === 'Rate' || kind === 'Spread' ? '(%)' : ''}
          </label>
          <input
            type="number"
            step="any"
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder={kind === 'Rate' ? 'e.g., 3.50' : 'e.g., 100.25'}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Label</label>
          <input type="text" value={label} onChange={e => setLabel(e.target.value)} placeholder="Human-readable name" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Currency</label>
          <select value={currency} onChange={e => setCurrency(e.target.value)} className={inputClass}>
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>Description</label>
          <input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional note" className={inputClass} />
        </div>
      </div>
      <div className="flex gap-2 mt-4 justify-end">
        <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-[#525252] bg-white border border-[#d4d4d4] rounded-lg hover:bg-[#f5f5f5]">
          Cancel
        </button>
        <button onClick={handleSubmit} className={formStyles.primaryButton}>
          Save
        </button>
      </div>
    </div>
  );
}
